// ============================================================================
// QrCodeModal — show a QR code for a URL, with a one-click PNG download.
// ============================================================================
// Used in two places:
//   * EventDetailPage: encodes the per-event page URL (deep-linkable)
//   * MainApp homepage: encodes the events landing URL (permanent)
//
// The QR is rendered to a hidden <canvas> via qrcode.react's QRCodeCanvas
// so the download button can grab the data URL. The visible QR is the
// same component at a smaller size with white padding so it scans well
// when printed or photographed off a phone.
// ============================================================================

import { useRef } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { Download, Copy, Check } from 'lucide-react';
import { useState } from 'react';
import { Modal } from './Modal';

interface Props {
  open: boolean;
  onClose: () => void;
  url: string;
  title?: string;
  filename?: string;
}

export function QrCodeModal({ open, onClose, url, title = 'QR code', filename = 'qr-code' }: Props) {
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const [copied, setCopied] = useState(false);

  const handleDownload = () => {
    const canvas = canvasRef.current?.querySelector('canvas');
    if (!canvas) return;
    const dataUrl = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `${filename}.png`;
    a.click();
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard API not available — silently fail
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={title} maxWidth="max-w-md">
      <div className="space-y-4">
        <div ref={canvasRef} className="flex justify-center">
          <div className="bg-white p-4 rounded-md border border-gray-200 inline-block">
            <QRCodeCanvas
              value={url}
              size={240}
              bgColor="#ffffff"
              fgColor="#0F6E56"
              level="M"
              includeMargin={false}
            />
          </div>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-xs text-gray-700 break-all font-mono">
          {url}
        </div>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={handleCopy}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
          >
            {copied ? <Check className="h-4 w-4 text-uif-green-dark" /> : <Copy className="h-4 w-4" />}
            {copied ? 'Copied!' : 'Copy URL'}
          </button>
          <button
            type="button"
            onClick={handleDownload}
            className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white px-3 py-1.5 text-sm font-semibold hover:bg-uif-green-dark"
          >
            <Download className="h-4 w-4" />
            Download PNG
          </button>
        </div>

        <p className="text-xs text-gray-500 text-center">
          Tip: download the PNG and embed it in flyers, ads, or signage. Scanning it opens the page directly.
        </p>
      </div>
    </Modal>
  );
}
